// What time it is, and what has to happen because time passed. The normative
// schema for ICD-S05 Clock.
//
// WHY THE CLOCK IS A CONTRACT AND NOT A CALL TO `Date.now()`. Every screen in
// a fantasy league is a function of an instant: a slot is locked or it is not,
// a period is live or final, a deadline has passed or has not. If that instant
// is read from the ambient clock wherever it is needed, then nothing can be
// tested at a moment other than now, the tutorial cannot move time, and two
// parts of one response can disagree about what time it is. So `now` is a
// parameter everywhere, supplied by a `Clock`, and the only place the real one
// is read is the edge.
//
// THE THREE THINGS IN HERE ARE NOT ONE THING, and the document says so:
//
//   the CALENDAR    when a league's periods and lineup windows are
//   the READING     what is true at an instant, computed and never stored
//   the TICK        the work that must happen with nobody watching
//
// The first two are pure. Only the third needs infrastructure, and it needs
// much less of it than "a clock" sounds like, because nothing in this product
// has to FIRE at a lock — a lock is computed from a kickoff that already
// passed. What has to fire is the bookkeeping that outlives the page view.
//
// WHAT IS DELIBERATELY ABSENT. No `Date`, no epoch milliseconds, and no
// stored UTC offset anywhere in a view. Instants are ISO strings, and a rule
// written in wall-clock time ("Wednesday at 03:00") is resolved through the
// league's IANA zone at the instant in question — an offset stored in March is
// wrong in November, and a waiver run that moves an hour twice a year is the
// bug this rules out by construction.

import { z } from 'zod';

const Iso = z.string();
const PeriodIndex = z.number().int().min(1).max(60);

/**
 * What time is it.
 *
 * The seam, and the whole of it. `packages/identity` has carried a private
 * copy of this interface since 2026-09-14 with the comment "a local seam until
 * S-05 exists", and its conformance suite already moves a test clock past a
 * session's expiry — which is the proof that the shape is right, since the
 * check that needed it could not otherwise have been written.
 */
export interface Clock {
  now(): Date;
}

/** Where a period sits relative to an instant. The simulator's three states. */
export const PERIOD_STATUSES = ['pre', 'live', 'final'] as const;
export const PeriodStatus = z.enum(PERIOD_STATUSES);
export type PeriodStatusId = (typeof PERIOD_STATUSES)[number];

/**
 * The closed set of things that happen because time passed.
 *
 * Closed on purpose, like the fact store's topics and roles: a job kind that
 * is a free string is a job kind nobody can enumerate, and the sweep's whole
 * correctness argument is that the set of due work is knowable.
 *
 * `draft.pick.expire` is here for the SLOW draft, whose pick clock is measured
 * in hours and has to survive everybody closing the tab. A live draft room's
 * pick clock is the Durable Object's own business and is not a job.
 */
export const JOB_KINDS = [
  // THE CLOCK'S OWN: plan this league's due work ahead (DATA_LAYER_DESIGN
  // §4.5, ICD-S05 §4). Once a day, and whenever a request changes what the
  // planner reads — a settings save, a trade's answer-by, a vote opened — so
  // the minute sweep reads what is due instead of working it out for every
  // league. Never planned by the calendar: it is written by the request that
  // changes the league, by the job that ran before it, and by itself.
  'clock.plan',
  'period.open',
  'lineup.lock',
  'matchup.score',
  'matchup.finalize',
  'stats.correct',
  // The matchup cue's table (`league_versus`), computed again by the clock
  // rather than by whichever request found it stale (DATA_LAYER_DESIGN §4.3,
  // Phase 1): at each landing of the sport's lines for a league in season,
  // and whenever a planning run finds the kept table missing or behind.
  'league.versus.refresh',
  'waivers.process',
  // A league's trades at each instant one has something due: an offer's
  // answer-by, a review window's end, a cleared trade's execution
  // (PLAN-2026-09-26-trades T3). Its instants come from the trades' own rows,
  // not the calendar, so its handler reports them (`JobHandler.instants`).
  'trades.advance',
  'trades.deadline',
  // A rule change put to the league's vote, counted when its time is up (B6).
  // Its instants come from the open vote's row, as `trades.advance`'s do.
  'rules.vote.close',
  // The two reminders a draft sends before it happens (K3's draft half, built
  // with D6). They are jobs rather than a cron of their own because the
  // instant they are due at is derived from a league's own draft time, which
  // is exactly what the planner is for.
  'notify.draft.plan',
  'notify.draft.remind',
  // The lineup warning before the week's first lock (D3, NOTIFICATIONS.md
  // §3.4): one job per lead time a manager can choose, each telling the
  // managers who chose it, and a last call ten minutes before the lock to
  // anybody whose lineup still has a slot that cannot score.
  'notify.lineup.warn',
  'notify.lineup.last',
  'draft.start',
  'draft.pick.expire',
  // A drafted league gets its schedule and its standings (PLAN-2026-09-24
  // item C). A job of its own rather than a second transaction inside the
  // draft, so a draft that succeeded is never undone by a schedule that could
  // not be written, and a schedule that could not be written is tried again.
  'season.open',
  'period.close',
  'season.rollover',
  // The bots of a league that has any (AI_MANAGERS.md §7, B-292 to B-294):
  // an hour before each kickoff that locks somebody, every bot's lineup —
  // nobody who cannot score where somebody can, and its best for a bot that
  // sets one; and once a day in the league's zone, a bot's look at its team —
  // the wire, its lineup, a trade offer — as often as its style says.
  'bots.lineup',
  'bots.checkin',
] as const;
export const JobKind = z.enum(JOB_KINDS);
export type JobKindId = (typeof JOB_KINDS)[number];

/**
 * The same vocabulary the ingest ledger settled on, and for the same reason.
 *
 * `failed` and `refused` are alarms, `partial` is a queue with a number, `ok`
 * and `idle` are green (HANDOFF.md, `0005`). `running` is here because the
 * ingest worker learned in Phase 4 that a job with no claimed state is a job
 * two sweeps run at once, and a `running` row older than its lease is closed
 * as `failed` rather than blocking its league forever.
 */
export const JOB_STATUSES = ['running', 'ok', 'idle', 'partial', 'refused', 'failed'] as const;
export const JobStatus = z.enum(JOB_STATUSES);
export type JobStatusId = (typeof JOB_STATUSES)[number];

/**
 * One lineup window, as of an instant.
 *
 * A window is the unit lineups are keyed by — `lineup_slots.window_id` and
 * `player_period_stats.window_id` both hold `LineupWindow.id` — which is why
 * the calendar is upstream of scoring and not merely beside it.
 */
export const WindowState = z.object({
  id: z.string().min(1),
  start: Iso,
  end: Iso,
  firstKickoff: Iso.nullable(),
  lockAt: Iso.nullable(),
  locked: z.boolean(),
});

/** A league's period, as of an instant. */
export const PeriodState = z.object({
  season: z.number().int(),
  index: PeriodIndex,
  label: z.string().min(1),
  kind: z.enum(['regular', 'playoff', 'consolation', 'off']),
  start: Iso,
  end: Iso,
  status: PeriodStatus,
  windows: z.array(WindowState).min(1),
  proBye: z.array(z.string().min(1)),
  playoffEligible: z.boolean(),
  frozen: z.boolean(),
});

/**
 * What time it is for one league — everything a screen needs to render a
 * countdown, a lock badge or a deadline without doing date arithmetic itself.
 */
export const ClockView = z.object({
  now: Iso,
  timeZone: z.string().min(1),
  current: PeriodState.nullable(),
  next: PeriodState.nullable(),
  nextLockAt: Iso.nullable(),
  nextWaiverRunAt: Iso.nullable(),
  tradeDeadlineAt: Iso.nullable(),
  draftAt: Iso.nullable(),
  behindBy: z.number().int().nullable(),
});

/**
 * How fresh a period's stat lines are, and since when (BACKLOG L2,
 * PLAN-2026-09-25 N5 and rule 9). Freshness is SAID, never implied by a zero:
 * a game that has kicked off and whose lines have not landed leaves a score
 * of 0 on the board, and 0 reads as a score unless something says otherwise.
 *
 *   none     no game in the period has kicked off; nothing to be fresh about
 *   delayed  a game has kicked off whose lines have not landed, and no live
 *            poll is newer than `STATS_DELAYED_AFTER_MS`
 *   live     the same, with a live poll newer than that — never produced
 *            until a live feed exists (BACKLOG L0)
 *   final    every game that has kicked off has its lines
 *
 * `asOf` is when the numbers were last brought up to date: the last `ok` or
 * `partial` stats run for the sport (`statsRunResource`), or the last live
 * poll when that is later. Null for `none`, and when no run has ever finished.
 */
export const STATS_STATES = ['none', 'delayed', 'live', 'final'] as const;
export const StatsState = z.enum(STATS_STATES);
export type StatsStateId = (typeof STATS_STATES)[number];

export const StatsFreshness = z.object({
  state: StatsState,
  asOf: Iso.nullable(),
});

/**
 * How old a live poll may be before a game in progress reads as delayed:
 * five minutes, BACKLOG L2's recommendation. A live feed that polls once a
 * minute (L4) has missed four in a row by then, which is a feed in trouble
 * rather than a slow minute.
 */
export const STATS_DELAYED_AFTER_MS = 5 * 60_000;

/** A unit of work that became due at an instant. */
export const ScheduledJob = z.object({
  id: z.string().min(1),
  leagueId: z.string().min(1),
  kind: JobKind,
  dueAt: Iso,
  period: PeriodIndex.nullable(),
  windowId: z.string().min(1).nullable(),
});

/** What happened when it ran. One row per attempt, never overwritten. */
export const JobOutcome = z.object({
  jobId: z.string().min(1),
  kind: JobKind,
  status: JobStatus,
  dueAt: Iso,
  ranAt: Iso,
  lateBy: z.number().int(),
  attempt: z.number().int().min(1),
  note: z.string().nullable(),
});

export type WindowStateShape = z.infer<typeof WindowState>;
export type PeriodStateShape = z.infer<typeof PeriodState>;
export type ClockViewShape = z.infer<typeof ClockView>;
export type ScheduledJobShape = z.infer<typeof ScheduledJob>;
export type JobOutcomeShape = z.infer<typeof JobOutcome>;
export type StatsFreshnessShape = z.infer<typeof StatsFreshness>;
